import { Component, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext.js';

interface FallbackCopy {
  title: string;
  message: string;
  reload: string;
}

interface ErrorBoundaryClassProps extends FallbackCopy {
  children: ReactNode;
}

interface ErrorBoundaryClassState {
  error: Error | null;
}

/**
 * Catches a render-time throw anywhere below it and shows a recoverable
 * Spanish message with a reload action instead of a blank page. This is what
 * bugs 2 and 3 (reports/shifts rendering blank on a shape mismatch) were
 * missing: the throw was real, but nothing above it in the tree could catch
 * it, so React unmounted the whole page silently.
 *
 * React only supports error boundaries as class components (no hook
 * equivalent exists as of React 19), so the class stays minimal and all
 * copy/localization is resolved by the functional wrapper below and passed
 * in as plain string props.
 */
class ErrorBoundaryClass extends Component<ErrorBoundaryClassProps, ErrorBoundaryClassState> {
  override state: ErrorBoundaryClassState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryClassState {
    return { error };
  }

  override componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // eslint-disable-next-line no-console -- last-resort surface; there is no
    // structured browser-side log sink in this SPA (see STATUS.md).
    console.error('admin route crashed', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="error-boundary" role="alert">
          <h1>{this.props.title}</h1>
          <p>{this.props.message}</p>
          <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
            {this.props.reload}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Route-level wrapper: keyed on the current pathname so navigating to a
 * different page after a crash remounts a fresh boundary instead of being
 * stuck on the fallback forever.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const location = useLocation();
  return (
    <ErrorBoundaryClass
      key={location.pathname}
      title={t('error.boundaryTitle')}
      message={t('error.boundaryMessage')}
      reload={t('error.reload')}
    >
      {children}
    </ErrorBoundaryClass>
  );
}
